import { URLS } from '../config';
import { createRequestQueueService, type RequestQueueService } from '../queue/requestQueue';
import type { TokenRefreshService } from '../services/tokenRefresh';
import {
  createApolloTokenRefreshService
} from '../services/tokenRefresh';
import type { TokenStorageManager } from '../services/tokenStorage';
import { tokenStorageManager } from '../services/tokenStorage';
import { requestChecker, RequestChecker } from '../utils/requestChecker';


interface InterceptorDependencies {
  tokenStorage: TokenStorageManager;
  tokenRefresh: TokenRefreshService;
  queueRequest: RequestQueueService;
  requestChecker: RequestChecker;
  originalFetch: typeof window.fetch;
}

const redirectToLogin = () => {
  window.location.href = URLS.LOGIN_EXPIRED
}

const createInterceptor = ({ originalFetch, queueRequest, requestChecker, tokenRefresh, tokenStorage }: InterceptorDependencies) => {
  let isRefreshingToken = false

  const interceptedFetch = async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input?.url;
    const body = typeof init.body === "string" ? init.body : undefined;

    if (!requestChecker.isBffRequest(url)) {
      return originalFetch(input, init);
    }

    // Avoid refresh token request loop
    if (requestChecker.isRefreshTokenRequest(body)) {
      return originalFetch(input, init);
    }

    if (tokenStorage.skipRefreshToken()) {
      return originalFetch(input, init);
    }

    if (isRefreshingToken) {
      const skip = requestChecker.isRefreshTokenRequest(body)
      return new Promise<Response>((resolve, reject) => {
        queueRequest.addToQueue({ input, init, resolve, reject, skip })
      });
    }

    isRefreshingToken = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const storage = tokenStorage.getStorage()!
      const { accessToken, refreshToken } = await tokenRefresh.refresh(storage.token, storage.refreshToken)
      tokenStorage.updateTokens(accessToken, refreshToken)
      await queueRequest.processQueue(accessToken, originalFetch);
      const initWithHeadersUpdated = tokenRefresh.updateHeader(init, accessToken)
      return originalFetch(input, initWithHeadersUpdated);
    } catch (error) {
      queueRequest.rejectAllQueue(error)
      redirectToLogin()
      tokenStorage.clearStorage()
      return originalFetch(input, init);
    } finally {
      isRefreshingToken = false;
    }
  }
  return interceptedFetch
};

export const overrideFetch = (): void => {
  const originalFetch = window.fetch;

  const dependencies: InterceptorDependencies = {
    tokenStorage: tokenStorageManager(),
    tokenRefresh: createApolloTokenRefreshService(),
    queueRequest: createRequestQueueService(),
    requestChecker: requestChecker(),
    originalFetch
  };

  window.fetch = createInterceptor(dependencies);
};
